import Controller from '@ember/controller';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { FirebaseError } from 'firebase/app';
import { type PhoneMultiFactorInfo, RecaptchaVerifier } from 'firebase/auth';
import type MfaRoute from 'frontend-login/routes/mfa';
import type { ModelFrom } from 'frontend-login/utils/routes';
import { formatPhoneNumber } from 'frontend-utils/utils/phone-number-utils';
import nextWithDefault from 'password-form/utils/auth-utils';
import {
  FIREBASE_ERROR_CODES,
  MFA_PHONE_FACTOR_ID,
  MFA_TYPES,
  MFA_VERIFICATION_CODE_LENGTH,
  type MFAType,
} from 'password-form/utils/constant-values';
import NoActiveUserError from 'password-form/utils/no-active-user-error';

export default class MFAController extends Controller {
  declare model: ModelFrom<MfaRoute>;
  queryParams = ['next'];

  @service declare errorTracking: Services['error-tracking'];
  @service declare firebaseService: Services['firebase-service'];
  @service declare intl: Services['intl'];
  @service declare mfaService: Services['mfa-service'];

  @tracked captchaCode = '';
  @tracked errorMessage = '';
  @tracked errorTitle = '';
  @tracked hasValidPhoneNumber = false;
  @tracked isCaptchaInVerificationCodeEnabled = false;
  @tracked isContinueForSelectionLoading = false;
  @tracked isContinueForVerificationLoading = false;
  @tracked isVerificationCodeValid = true;
  @tracked loginCaptchaResolved = false;
  @tracked next: string | undefined;
  @tracked phoneNumber = '';
  @tracked radioMfaType: MFAType = MFA_TYPES.MOBILE;
  @tracked selectedCountryCode = {
    code: 'US',
    phoneCode: '1',
  };
  @tracked showAuthenticatorCode = false;
  @tracked showEnrollmentVerificationStep = false;
  @tracked showError = false;
  @tracked totpSecret = '';
  @tracked verificationCode = '';

  captcha: RecaptchaVerifier | null = null;
  captchaElement: HTMLDivElement | null = null;

  cleanupCaptcha = (removeHtmlElement = false): void => {
    if (this.captcha) {
      try {
        this.captcha.clear();
      } catch {
        // Silent fail - captcha may already be cleared
      }
    }

    this.captcha = null;
    if (removeHtmlElement && this.captchaElement) {
      this.captchaElement = null;
    }
  };
  // Only used for testing purposes
  enableCaptcha = true;
  initializeRecaptcha = modifier((element: HTMLDivElement): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.createCaptcha(element);
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPhoneNumber() {
    return formatPhoneNumber(
      this.selectedCountryCode.phoneCode,
      this.phoneNumber,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isContinueForSelectionDisabled() {
    if (this.mfaType === MFA_TYPES.SMS) {
      return (
        this.phoneNumber === '' ||
        this.captchaCode === '' ||
        !this.hasValidPhoneNumber
      );
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isContinueForVerificationDisabled() {
    const hasEmptyVerificationCode =
      !this.showCaptchaIntermediateScreen && this.verificationCode === '';
    const hasInvalidCaptcha =
      this.showCaptchaIntermediateScreen && this.captchaCode === '';
    return hasEmptyVerificationCode || hasInvalidCaptcha;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUserMfaEnforced() {
    return this.model.mfaInfo.enforce_mfa === true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mfaType() {
    return this.mfaService.resolver
      ? this.model.mfaInfo.gip_display_name
      : this.radioMfaType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showCaptchaIntermediateScreen() {
    return (
      this.model.mfaInfo.gip_display_name === MFA_TYPES.SMS &&
      this.mfaService.isLoginVerificationActive &&
      !this.loginCaptchaResolved
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showVerificationStep() {
    return (
      this.model.mfaInfo.gip_display_name !== null ||
      this.showEnrollmentVerificationStep
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    if (this.mfaService.isLoginVerificationActive) {
      return this.intl.t('mfa.multi_factor_authentication');
    }

    return this.intl.t('mfa.title');
  }

  // Value that is used to request the verification code
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get verificationValue() {
    const { resolver } = this.mfaService;
    const isPhoneFactor = resolver?.hints[0]?.factorId === MFA_PHONE_FACTOR_ID;

    if (this.mfaType === MFA_TYPES.EMAIL) {
      return this.model.email || '';
    }

    if (this.mfaService.isLoginVerificationActive && isPhoneFactor) {
      return (resolver.hints[0] as PhoneMultiFactorInfo).phoneNumber;
    }

    return this.formattedPhoneNumber;
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    registerDestructor(this, () => this.cleanupCaptcha(true));
  }

  async enrollEmail(): Promise<void> {
    try {
      this.isContinueForSelectionLoading = true;
      await this.mfaService.startEnrollGip();
      this.showEnrollmentVerificationStep = true;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isContinueForSelectionLoading = false;
    }
  }

  async enrollMobile(): Promise<void> {
    try {
      this.isContinueForSelectionLoading = true;
      const totpSecret = await this.mfaService.startEnrollGip();
      if (totpSecret) {
        this.totpSecret = totpSecret.secretKey;
      }
      this.showAuthenticatorCode = true;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isContinueForSelectionLoading = false;
    }
  }

  handleError(error: unknown): void {
    if (error instanceof FirebaseError || error instanceof NoActiveUserError) {
      if (
        error.code === FIREBASE_ERROR_CODES.REQUIRES_RECENT_LOGIN ||
        error.code === FIREBASE_ERROR_CODES.NO_ACTIVE_USER
      ) {
        window.location.href = `/login?next=${nextWithDefault(this.next)}&isLoginExpired=true`;
        return;
      }
      // Special handling for captcha errors
      if (
        error.code === FIREBASE_ERROR_CODES.CAPTCHA_CHECK_FAILED ||
        error.message.includes('DUPE')
      ) {
        // Log the specific error for tracking
        this.errorTracking.captureException(
          new Error(`Captcha error in verification: ${error.message}`),
        );

        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('login.error.captcha_failed'),
        );

        // Recreate the captcha to allow the user to try again
        if (this.captchaElement) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.createCaptcha(this.captchaElement);
        }
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.INVALID_PHONE_NUMBER) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('error.invalid_phone_number'),
        );
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.OPERATION_NOT_ALLOWED) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('login.error.sms_not_available'),
        );
        this.errorTracking.captureException(error);
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.UNVERIFIED_EMAIL) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('login.error.unverified_email'),
        );
        this.errorTracking.captureException(error);
        return;
      }
    }
    this.showErrorMessage();
    this.errorTracking.captureException(error);
  }

  async handleSignInWithEnrolledMethod(
    verificationCode: string,
  ): Promise<void> {
    try {
      if (
        this.mfaService.resolver?.hints[0]?.factorId === MFA_PHONE_FACTOR_ID
      ) {
        await this.mfaService.signInWithSms(verificationCode, {
          next: nextWithDefault(this.next),
        });
        return;
      }
      await this.mfaService.signInWithTOTP(verificationCode, {
        next: nextWithDefault(this.next),
      });
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === FIREBASE_ERROR_CODES.INVALID_VERIFICATION_CODE
      ) {
        this.isVerificationCodeValid = false;
        return;
      }
      this.handleError(error);
    }
  }

  reset(): void {
    this.showEnrollmentVerificationStep = false;
    this.selectedCountryCode = {
      code: 'US',
      phoneCode: '1',
    };
    this.phoneNumber = '';
    this.verificationCode = '';
    this.showAuthenticatorCode = false;
    this.captchaCode = '';
  }

  @action
  async createCaptcha(element: HTMLDivElement | null = null): Promise<void> {
    // Clear any previous error state
    this.showError = false;

    // Clean up any existing captcha before creating a new one
    if (this.captcha) {
      this.cleanupCaptcha(true);
    }

    if (element) {
      this.captchaElement = element;
    }
    if (!this.captchaElement || !this.enableCaptcha) {
      return;
    }

    try {
      this.captcha = new RecaptchaVerifier(
        this.firebaseService.auth,
        this.captchaElement,
        {},
      );
      if (this.captcha) {
        await this.captcha.render();
        this.captchaCode = await this.captcha.verify();
        if (this.isCaptchaInVerificationCodeEnabled) {
          await this.mfaService.requestSms(
            `+${this.selectedCountryCode.phoneCode}${this.phoneNumber}`,
            this.captcha,
          );
          this.isCaptchaInVerificationCodeEnabled = false;
          // Disabling linting on this line, since this is a false negative.

          this.captcha.clear();
        }
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  onBackButtonClicked(): void {
    if (this.showEnrollmentVerificationStep && this.showAuthenticatorCode) {
      this.showEnrollmentVerificationStep = false;
      return;
    }
    this.reset();
  }

  @action
  onChangeMfaType(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.radioMfaType = target.value as MFAType;
  }

  @action
  async onContinueForSelectionClicked(): Promise<void> {
    if (this.isContinueForSelectionLoading) {
      return;
    }

    this.showError = false;
    switch (this.radioMfaType) {
      case MFA_TYPES.EMAIL: {
        await this.enrollEmail();
        break;
      }
      case MFA_TYPES.SMS: {
        try {
          if (
            this.hasValidPhoneNumber &&
            this.captchaCode !== '' &&
            this.captcha
          ) {
            this.isContinueForSelectionLoading = true;
            await this.mfaService.requestSms(
              `+${this.selectedCountryCode.phoneCode}${this.phoneNumber}`,
              this.captcha,
            );
            this.isContinueForSelectionLoading = false;
            this.showEnrollmentVerificationStep = true;
          }
        } catch (error) {
          this.handleError(error);
        } finally {
          this.isContinueForSelectionLoading = false;
        }

        break;
      }
      case MFA_TYPES.MOBILE: {
        if (!this.showAuthenticatorCode) {
          await this.enrollMobile();
          return;
        }

        this.showEnrollmentVerificationStep = true;
        break;
      }
      default:
        this.radioMfaType satisfies never;
    }
  }

  @action
  async onContinueForVerificationClicked(): Promise<void> {
    if (this.isContinueForVerificationLoading) {
      return;
    }

    this.showError = false;

    // If we are in the captcha intermediate screen, we need to resolve the captcha first
    if (this.showCaptchaIntermediateScreen) {
      if (this.captchaCode === '' || !this.captcha) {
        return;
      }

      this.isContinueForVerificationLoading = true;
      try {
        await this.mfaService.requestSms(null, this.captcha);
        this.loginCaptchaResolved = true;

        this.cleanupCaptcha();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isContinueForVerificationLoading = false;
      }
      return;
    }

    if (!this.isVerificationCodeValid || this.verificationCode === '') {
      return;
    }

    this.isContinueForVerificationLoading = true;

    if (this.mfaService.resolver) {
      await this.handleSignInWithEnrolledMethod(this.verificationCode);
      this.isContinueForVerificationLoading = false;
      return;
    }

    try {
      // If we don't have a resolver, we need to enroll
      switch (this.radioMfaType) {
        // TODO-MFA-EMAIL: currently we are not supporting email as a MFA method
        case MFA_TYPES.EMAIL: {
          //   await this.mfaService.verifyEmailForEnrollment(this.verificationCode);
          break;
        }
        case MFA_TYPES.SMS: {
          await this.mfaService.verifySmsForEnrollment(this.verificationCode);
          break;
        }
        case MFA_TYPES.MOBILE: {
          await this.mfaService.verifyAuthenticatorForEnrollment(
            this.verificationCode,
          );
          break;
        }
        default:
          this.radioMfaType satisfies never;
      }
      this.mfaService.redirectToLogin({ next: nextWithDefault(this.next) });
    } catch (error) {
      this.isVerificationCodeValid = false;
      this.errorTracking.captureException(error);
    } finally {
      this.isContinueForVerificationLoading = false;
    }
  }

  @action
  onCountryCodeChange(countryCode: { code: string; phoneCode: string }): void {
    this.selectedCountryCode = countryCode;
  }

  @action
  onPhoneNumberChange(phoneNumber: string): void {
    this.phoneNumber = phoneNumber;
  }

  @action
  onResendVerificationCode(): void {
    if (this.mfaType === MFA_TYPES.SMS) {
      this.isCaptchaInVerificationCodeEnabled = true;
    }
  }

  @action
  onSetupLater(): void {
    this.mfaService.redirectToLogin({ next: nextWithDefault(this.next) });
  }

  @action
  setVerificationCode(code: string): void {
    this.isVerificationCodeValid = false;
    this.verificationCode = code;
    // All verification codes are 6 digits.
    // We also allow an empty string because it will give errors otherwise when focus is lost(so automatically when a user goes to their mails)
    if (
      this.verificationCode.length === MFA_VERIFICATION_CODE_LENGTH ||
      this.verificationCode.length === 0
    ) {
      this.isVerificationCodeValid = true;
    }
  }

  @action
  showErrorMessage(
    title: string | null = this.intl.t('general.error_title'),
    message: string | null = this.intl.t('login.error.general'),
  ): void {
    this.errorMessage = '';
    this.errorTitle = '';
    if (title) {
      this.errorTitle = title;
    }
    if (message) {
      this.errorMessage = message;
    }
    this.showError = true;
  }

  @action
  submitVerificationCodeWithEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.onContinueForVerificationClicked();
    }
  }

  @action
  updateIsPhoneNumberValid(isValid: boolean): void {
    this.hasValidPhoneNumber = isValid;
  }
}
